'use client';
import { BASE_URL } from '@/common/constants';
import { getWebsiteCodeHeader } from '@/common/pageConfig';
import Toast from '@/common/Toast';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import { startOfDay, endOfDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

const SurveyForm = ({ websiteCode, locale }) => {
  const [questions, setQuestions] = useState([]);
  const [serveySessions, setServeySessions] = useState([]);
  const [serveyContentId, setServeyContentId] = useState(null);
  const [titleServeyContent, setTitleVerveyContent] = useState('');
  const t = useTranslations('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDownloading, setIsDownloading] = useState(null);
  // lấy dữ liệu đợt khảo sát
  const fetchServeySession = async () => {
    const res = await fetch(
      `${BASE_URL}/serveySessions?startDate=${startOfDay(new Date(), { locale: vi }).toISOString()}&endDate=${endOfDay(new Date(), {
        locale: vi,
      }).toISOString()}&limit=9999&languageCode=${locale}&status=active`,
      {
        headers: {
          websiteCode: getWebsiteCodeHeader(websiteCode),
          'Content-Type': 'application/json',
        },
      },
    );
    const result = await res.json();
    setServeySessions(get(result, 'results', []));
  };
  // lấy dữ liệu nội dung
  const fetchFeedbacks = async () => {
    const res = await fetch(
      `${BASE_URL}/serveyContents?startDateSession=${startOfDay(new Date(), { locale: vi }).toISOString()}&endDateSession=${endOfDay(new Date(), {
        locale: vi,
      }).toISOString()}&serveySessionId=${serveyContentId}&limit=9999&sortBy=numberIndex:asc`,
      {
        headers: {
          websiteCode: getWebsiteCodeHeader(websiteCode),
          'Content-Type': 'application/json',
        },
      },
    );
    const result = await res.json();
    setQuestions(
      flatMap(
        get(result, 'results', []).map((item) => {
          const questions = JSON.parse(item?.questions || '[]');
          return questions.map((q) => ({
            ...q,
            id: item.id,
            personAnswers: item?.personAnswers,
            isRequired: item?.isRequired,
            personAnswersDate: item?.personAnswersDate,
            fileUrls: item?.fileUrls,
          }));
        }),
      ),
    );

    // setServeyContent(get(result, 'results', []));
  };

  useEffect(() => {
    fetchServeySession();
  }, []);

  // const fetchGetOneFeedbacks = async (id) => {
  //   const res = await fetch(`${BASE_URL}/serveyContents/${id}`);
  //   const result = await res.json();
  //   const mergedQuestions = JSON.parse(get(result, 'questions', ''));
  //   setQuestions(mergedQuestions);
  //   // setTotalPages(mergedQuestions?.length);
  // };

  useEffect(() => {
    if (serveyContentId) {
      // fetchGetOneFeedbacks(serveyContentId);
      fetchFeedbacks();
    }
  }, [serveyContentId]);

  const handleAnswerChange = (index, value) => {
    const updated = [...questions];
    updated[index].answer = value;
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, value) => {
    const updated = [...questions];
    updated[qIndex].answer = value;
    setQuestions(updated);
  };

  const handleOptionsChange = (qIndex, value) => {
    const updated = [...questions];
    const currentAnswers = updated[qIndex].answers || [];

    if (currentAnswers.includes(value)) {
      // Nếu đã tồn tại → bỏ chọn
      updated[qIndex].answers = currentAnswers.filter((ans) => ans !== value);
    } else {
      // Nếu chưa tồn tại → thêm vào
      updated[qIndex].answers = [...currentAnswers, value];
    }

    setQuestions(updated);
  };

  function renderTypeQues(data, index) {
    if (data?.type === 'essay') {
      return (
        <textarea
          id="answers"
          rows="4"
          value={data.answer}
          onChange={(e) => handleAnswerChange(index, e.target.value)}
          className="block p-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300"
          placeholder={t('survey.answerHere')}></textarea>
      );
    }
    if (data?.type === 'multipleChoice') {
      return (
        <>
          {data.options.map((opt, idx) => {
            const inputId = `question-${index}-option-${idx}`; // id duy nhất
            return (
              <div key={idx} className="flex items-center mb-4">
                <input
                  checked={data?.answer === opt}
                  value={opt}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  id={inputId}
                  type="radio"
                  name={`question-${index}`} // nhóm theo câu hỏi
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 "
                />
                <label htmlFor={inputId} className="ms-2 text-sm font-medium text-gray-900 ">
                  {opt}
                </label>
              </div>
            );
          })}
        </>
      );
    }
    if (data?.type === 'multipleChoices') {
      return (
        <>
          {data.options.map((opt, idx) => {
            const inputId = `option-${index}-${idx}`;

            return (
              <div key={idx} className="flex items-center mb-4">
                <input
                  checked={data?.answers?.includes(opt)}
                  value={opt}
                  onChange={(e) => handleOptionsChange(index, e.target.value)}
                  id={inputId}
                  type="checkbox"
                  name={inputId}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 "
                />
                <label htmlFor={inputId} className="ms-2 text-sm font-medium text-gray-900 ">
                  {opt}
                </label>
              </div>
            );
          })}
        </>
      );
    }
  }

  function safeParseArray(input) {
    try {
      const parsed = JSON.parse(input);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function addNewData(d) {
    let dataTemp = [];

    const type = get(d, 'type', '');

    if (type === 'essay' || type === 'multipleChoice') {
      const personAnswers = safeParseArray(get(d, 'personAnswers', '[]'));
      const answer = get(d, 'answer', '');
      dataTemp = [...personAnswers.map(String), String(answer)];
    }

    if (type === 'multipleChoices') {
      const personAnswers = safeParseArray(get(d, 'personAnswers', '[]'));
      const answers = get(d, 'answers', []);
      dataTemp = [...personAnswers.map(String), ...(Array.isArray(answers) ? answers.map(String) : [])];
    }

    return dataTemp;
  }

  function addNewDataDate(d, dateTime) {
    let dataTemp = [];

    const type = get(d, 'type', '');

    if (type === 'essay' || type === 'multipleChoice') {
      const personAnswers = safeParseArray(get(d, 'personAnswersDate', '[]'));

      dataTemp = [...personAnswers.map(String), String(dateTime)];
    }

    if (type === 'multipleChoices') {
      const personAnswers = safeParseArray(get(d, 'personAnswersDate', '[]'));
      const answers = get(d, 'answers', []);
      dataTemp = [...personAnswers.map(String), ...(Array.isArray(answers) ? dateTime.map(String) : [])];
    }

    return dataTemp;
  }

  function validateQuestions() {
    const newErrors = {};

    questions.forEach((item, index) => {
      if (!item.isRequired) return;

      if (item.type === 'essay') {
        if (!item.answer || item.answer.trim() === '') {
          newErrors[index] = t('survey.essayAnwer');
        }
      }

      if (item.type === 'multipleChoice') {
        if (!item.answer) {
          newErrors[index] = t('survey.multipleChoiceAnwer');
        }
      }

      if (item.type === 'multipleChoices') {
        if (!item.answers || item.answers.length === 0) {
          newErrors[index] = t('survey.multipleChoicesAnwer');
        }
      }
    });

    return newErrors;
  }

  async function handleSaveQues() {
    try {
      const validationErrors = validateQuestions();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});
      const dateNow = new Date().toISOString();
      questions.forEach(async (item) => {
        const payload = {
          personAnswers: JSON.stringify(addNewData(item)),
          personAnswersDate: JSON.stringify(addNewDataDate(item, dateNow)),
        };

        await fetch(`${BASE_URL}/serveyContents/updateAnwser/${item?.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      });
      setServeyContentId(null);
      setQuestions([]);
      setToastMessage(t('survey.notifications'));
      setShowToast(true);
    } catch (error) {
      alert(`Lưu câu hỏi thất bại!${error}`);
    }
  }

  const downloadFiles = async (id) => {
    if (!id || isDownloading !== null) return;
    setIsDownloading(id);
    try {
      const response = await fetch(`${BASE_URL}/serveyContents/downloadFiles/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Tài liệu đính kèm ${titleServeyContent}.zip`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Tải file thất bại!');
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="mb-8">
      {!serveyContentId && (
        <>
          <h2 className="text-xl font-bold mb-4 text-gray-800 ">{t('survey.selectRound')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {serveySessions?.map((item) => (
              <div
                key={item.id}
                onClick={() => {
                  setServeyContentId(item.id);
                  setTitleVerveyContent(item?.name);
                }}
                className={`cursor-pointer p-4 rounded-lg border 
          ${serveyContentId === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          hover:shadow transition duration-300`}>
                <h3 className="font-semibold text-gray-800 ">{item.name}</h3>
              </div>
            ))}
          </div>
        </>
      )}
      {/* FORM KHẢO SÁT */}
      {serveyContentId && (
        <div className=" transition-all duration-500">
          <h2 className="text-xl font-bold text-gray-800 mb-5 uppercase flex">
            <button className="text-xl font-bold text-gray-800 mb-5 uppercase flex cursor-pointer" onClick={() => setServeyContentId(null)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="26" viewBox="0 0 20 21" fill="none">
                <path
                  d="M12.2108 16.3332C11.9167 16.3331 11.6347 16.2103 11.4268 15.9918L6.99129 11.3308C6.78341 11.1123 
              6.66663 10.816 6.66663 10.507C6.66663 10.198 6.78341 9.90169 6.99129 9.68318L11.4268 5.02224C11.5291 
              4.91094 11.6515 4.82217 11.7867 4.76111C11.922 4.70004 12.0675 4.66789 12.2148 4.66655C12.362 4.6652 
              12.508 4.69469 12.6443 4.75327C12.7806 4.81186 12.9044 4.89839 13.0085 5.00779C13.1126 5.1172 13.195 
              5.2473 13.2507 5.3905C13.3065 5.53371 13.3345 5.68714 13.3333 5.84186C13.332 5.99658 13.3014 6.14948 
              13.2433 6.29165C13.1852 6.43381 13.1007 6.56239 12.9948 6.66988L9.34322 10.507L12.9948 14.3441C13.1498
               14.5071 13.2554 14.7147 13.2981 14.9407C13.3409 15.1667 13.3189 15.4009 13.235 15.6138C13.1511 15.8267 
               13.009 16.0087 12.8267 16.1367C12.6444 16.2648 12.4301 16.3331 12.2108 16.3332Z"
                  fill="#000000E0"
                />
              </svg>
              {t('survey.surveyInfomations')} ({titleServeyContent})
            </button>
          </h2>
          {questions.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded border border-gray-200 shadow-sm  mb-4 p-2">
              <div className="font-medium flex flex-wrap items-start gap-x-4 justify-between">
                <div className="min-w-0 text-xl">
                  {item?.question} {item?.isRequired === true ? <span className="text-red-600">*</span> : ''}
                </div>
                {(item?.fileUrls ?? []).length > 0 && (
                  <button
                    onClick={() => downloadFiles(item.id)}
                    disabled={isDownloading === item.id}
                    className="text-blue-500 flex items-center gap-1 text-sm underline min-w-[160px] mt-2 sm:mt-0">
                    {isDownloading === item.id ? (
                      <svg width="18" height="18" className="animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#2b7fff">
                        <path d="M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="#2b7fff">
                        <path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-288-128 0c-17.7 0-32-14.3-32-32L224 0 64 0zM256 0l0 128 128 0L256 0zM112 256l160 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-160 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64l160 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-160 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64l160 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-160 0c-8.8 0-16-7.2-16-16s7.2-16 16-16z" />
                      </svg>
                    )}
                    Tải tệp đính kèm
                  </button>
                )}
              </div>
              <div style={{ marginTop: 16 }}>{renderTypeQues(item, index)}</div>
              {errors[index] && <p className="text-sm text-red-500 mt-2">{errors[index]}</p>}
            </div>
          ))}
          {questions?.length ? (
            <div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveQues}
                  className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br 
                                focus:ring-4 focus:outline-none focus:ring-blue-300  font-medium rounded-lg 
                                text-sm px-5 py-2.5 text-center me-2 mt-4 !ml-auto cursor-pointer">
                  {t('survey.submit')}
                </button>
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      )}
      <Toast message={toastMessage} show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default SurveyForm;
